import { useRender } from '@base-ui/react/use-render';
import { Label } from '@repo/ui/components/label';
import { cn } from '@repo/ui/lib/utils';
import * as React from 'react';
import {
  Controller,
  type ControllerProps,
  type FieldPath,
  type FieldValues,
  FormProvider,
  useFormContext,
} from 'react-hook-form';

/**
 * The registry's `Form`, ported to Base UI.
 *
 * **It is not hand-written and it is not the registry's file either**, and that
 * needs saying because `docs/ui-conventions.md` forbids the first and CLAUDE.md
 * assumes the second. `shadcn add form` against `base-luma` succeeds and writes
 * nothing: the style's `form.json` is a two-line stub carrying a name and a type
 * and no `files` array at all, so there is no Base UI cut of this component to
 * pull. What follows is the registry's own `form.tsx` — the same six exports,
 * the same three ids per field, the same `useFormField` contract — with its two
 * Radix dependencies replaced:
 *
 * - `LabelPrimitive` becomes this package's `Label`, which is already a plain
 *   `<label>` and needs nothing from Radix.
 * - `Slot` becomes Base UI's `useRender`. That is the whole of the `asChild`
 *   difference: `FormControl` takes a `render` prop rather than wrapping a
 *   child, which is how every other composed control in this repo already
 *   reads — `<Button render={<Link />} />`.
 *
 * **`shadcn add -o` cannot overwrite this**, because the registry has nothing to
 * overwrite it with. If a Base UI cut ever lands upstream, take it and delete
 * this note rather than merging the two.
 */

const Form = FormProvider;

interface FormFieldContextValue<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> {
  name: TName;
}

const FormFieldContext = React.createContext<FormFieldContextValue | null>(null);

/**
 * One field, and the one place "touched" is made to mean what this app means by
 * it.
 *
 * **`mode: 'onTouched'` does not by itself fix the bug FE019 exists for**, and
 * it is worth writing down why rather than leaving the next person to discover
 * it the way this was discovered. react-hook-form counts a field as touched the
 * moment it is blurred, typed in or not — its `skipValidation` reads
 * `!(isTouched || isBlurEvent)`, so a blur event always validates. That is the
 * same rule as the bare `onBlur` the seven hand-rolled forms used: focus the
 * autofocused field, alt-tab away, and an empty required field goes red on a
 * form nobody has typed in.
 *
 * This app means something narrower by touched — **edited** — which is what
 * `SignIn`'s hand-rolled `Touched` map meant, and the behaviour FE019's
 * regression test pins. So a blur reaches the resolver only once the field is
 * dirty or has already reported. Everything else is unchanged: a field nobody
 * ever edited is still caught at submit, which is the right moment for it.
 *
 * It is here, in the component, rather than in the seven screens, because a rule
 * every form has to remember is a rule six of seven forms got wrong once
 * already.
 */
function FormField<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({ render, ...props }: ControllerProps<TFieldValues, TName>) {
  // Set when the person types and never cleared, which is the whole of the
  // difference between this and `fieldState.isDirty`. Dirty is a comparison
  // against the default value, so it goes false again the moment somebody
  // deletes what they typed — and "typed a code, thought better of it, deleted
  // it, tabbed away" is exactly the case that ought to say the field is empty.
  //
  // A ref rather than state: nothing renders from it, and re-rendering the
  // field on the first keystroke of every field would be a cost paid for
  // nothing.
  const edited = React.useRef(false);

  return (
    <FormFieldContext.Provider value={{ name: props.name }}>
      <Controller
        {...props}
        render={({ field, fieldState, formState }) =>
          render({
            field: {
              ...field,
              // One parameter rather than a spread of react-hook-form's own
              // `(...event: any[])`: every caller passes exactly one thing — a
              // change event, or the value a Switch or Select hands over — and
              // spreading an `any[]` is an unchecked call this package's lint
              // rightly refuses.
              onChange: (value: unknown) => {
                edited.current = true;
                field.onChange(value);
              },
              onBlur: () => {
                // `isTouched` as well, so the gate stays open once it has opened
                // — a field that has reported keeps reporting.
                if (edited.current || fieldState.isTouched) field.onBlur();
              },
            },
            fieldState,
            formState,
          })
        }
      />
    </FormFieldContext.Provider>
  );
}

interface FormItemContextValue {
  id: string;
}

const FormItemContext = React.createContext<FormItemContextValue | null>(null);

/**
 * The three ids a field needs, derived from one `useId` rather than written by
 * the caller.
 *
 * This is the part that earns the component. Seven hand-rolled forms each wrote
 * their own `aria-describedby` ternary and their own `-error` id, and the ones
 * that got it wrong got it wrong silently — a description that is never
 * announced looks identical on screen to one that is.
 */
function useFormField() {
  const fieldContext = React.useContext(FormFieldContext);
  const itemContext = React.useContext(FormItemContext);
  const { getFieldState, formState } = useFormContext();

  if (fieldContext === null) throw new Error('useFormField must be used within <FormField>');
  if (itemContext === null) throw new Error('useFormField must be used within <FormItem>');

  const fieldState = getFieldState(fieldContext.name, formState);
  const { id } = itemContext;

  return {
    id,
    name: fieldContext.name,
    formItemId: `${id}-form-item`,
    formDescriptionId: `${id}-form-item-description`,
    formMessageId: `${id}-form-item-message`,
    ...fieldState,
  };
}

function FormItem({ className, ...props }: React.ComponentProps<'div'>) {
  const id = React.useId();

  return (
    <FormItemContext.Provider value={{ id }}>
      {/* `space-y-2`, which is the rhythm every field in this app already had
          before there was a component to hold it. */}
      <div data-slot="form-item" className={cn('space-y-2', className)} {...props} />
    </FormItemContext.Provider>
  );
}

/**
 * A field's label, and the one place a required field is marked.
 *
 * **The marker is a property of the label, not of seven screens.** `required`
 * appeared nowhere in this application until FE019, and the reason it appeared
 * nowhere is that nothing owned it — every form was free to mark a field, or
 * not, and none did. Putting it here means a screen says `required` once and
 * gets the asterisk, the `required` attribute on the control, and the same
 * answer for a person reading and a person listening.
 *
 * The asterisk is `aria-hidden`: `required` on the control is what a screen
 * reader announces, and a star read aloud in the middle of a label is noise on
 * top of an announcement that already happened.
 */
function FormLabel({
  className,
  children,
  required = false,
  ...props
}: React.ComponentProps<typeof Label> & { required?: boolean }) {
  const { error, formItemId } = useFormField();

  return (
    <Label
      data-slot="form-label"
      // The caller's class comes last so `fieldLabel` — the signage lettering
      // every field in this app is set in, and already `text-muted-foreground` —
      // wins the colour through tailwind-merge. A label that turns red is not
      // this app's error language; the message under the field is.
      className={cn(error && 'text-destructive', className)}
      htmlFor={formItemId}
      {...props}
    >
      {children}
      {required && (
        <span aria-hidden className="text-destructive">
          *
        </span>
      )}
    </Label>
  );
}

/**
 * The control itself, wired to the ids above.
 *
 * Base UI's `render` prop rather than Radix's `asChild`: pass the element,
 * `useRender` merges these props into it. `aria-describedby` names the
 * description and, once there is one, the message — so the hint is still
 * announced when the field is refused rather than being replaced by the
 * refusal, which is what the hand-rolled forms did.
 */
function FormControl({
  render,
  'aria-describedby': describedBy,
  ...props
}: useRender.ComponentProps<'input'>) {
  const { error, formItemId, formDescriptionId, formMessageId } = useFormField();

  return useRender({
    render,
    props: {
      id: formItemId,
      // A caller's own `aria-describedby` is **appended, not honoured instead**.
      // Spreading it over the generated one would silently drop the message id
      // and the refusal would stop being announced — visible to nobody, because
      // it still renders on screen. The sign-in screen is the case: its staff
      // code is described by the arrival line, which sits above the form and
      // explains why the person is looking at it.
      'aria-describedby': [
        formDescriptionId,
        error === undefined ? null : formMessageId,
        describedBy,
      ]
        .filter(Boolean)
        .join(' '),
      'aria-invalid': error !== undefined,
      ...props,
    },
  });
}

/** The standing hint under a field: what a good answer looks like, always true. */
function FormDescription({ className, ...props }: React.ComponentProps<'p'>) {
  const { formDescriptionId } = useFormField();

  return (
    <p
      data-slot="form-description"
      id={formDescriptionId}
      className={cn('text-sm text-muted-foreground', className)}
      {...props}
    />
  );
}

/**
 * What is wrong with this field, in the resolver's words or the caller's.
 *
 * Renders nothing when there is nothing to say, so a screen never places error
 * text by hand and never has to remember to take it away again.
 */
function FormMessage({ className, children, ...props }: React.ComponentProps<'p'>) {
  const { error, formMessageId } = useFormField();
  const body = error ? String(error.message ?? '') : children;

  if (!body) return null;

  return (
    <p
      data-slot="form-message"
      id={formMessageId}
      className={cn('text-sm text-destructive', className)}
      {...props}
    >
      {body}
    </p>
  );
}

export {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  useFormField,
};
